/**
 * Hook for fetching and saving Care Queue resident tab configuration.
 *
 * Uses React Query for caching and the unified API service for requests.
 * Supports both main app and widget contexts via optional `api` prop.
 */

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, type ApiService } from "../services/apiService";
import type { ConfigurationState } from "../types/configuration";
import { isInTransientOutage, transientErrorRefetchInterval } from "../utils/transientErrorRefetch";
import {
  mapApiResponseToConfig,
  mapConfigToApiRequest,
} from "../types/configuration";

const QUERY_KEY = ["care-queue-resident-tab-config"];
const RISK_CONFIG_QUERY_KEY = ["care-queue-resident-tab-risk-config"];

interface UseResidentTabConfigurationOptions {
  /** Only fetch when true (e.g., when modal is open) */
  enabled?: boolean;
  /** Custom API service instance (for widget usage with different auth) */
  api?: ApiService;
}

interface UseResidentTabConfigurationResult {
  /** The resolved configuration (camelCase frontend shape) */
  config: ConfigurationState | undefined;
  /** True during initial fetch */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Save configuration to the backend */
  saveConfig: (config: ConfigurationState) => Promise<void>;
  /** True while save is in progress */
  isSaving: boolean;
  /** Error if save failed */
  saveError: Error | null;
}

export function useResidentTabConfiguration({
  enabled = false,
  api,
}: UseResidentTabConfigurationOptions = {}): UseResidentTabConfigurationResult {
  const queryClient = useQueryClient();
  const service = api ?? apiService;

  // After a 5xx or a network failure both reads ask again (bounded, and on a tab return),
  // so the modal clears by itself once the API is back (LIIP-1036). The config read reports
  // a 5xx to onError for the load that starts the outage only, not for the automatic
  // repeats (contract C12, onError frequency unchanged).
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal, client, queryKey }) => {
      const configResponse = await service.getResidentTabConfiguration(signal, {
        reportServerError: !isInTransientOutage(client, queryKey),
      });
      return mapApiResponseToConfig(configResponse.config);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // No retry: the bounded repeats ask again after a transient error, one request per
    // repeat (PR #740 review item 9); a 401 waits for the host's new token.
    retry: false,
    refetchInterval: (q) => transientErrorRefetchInterval(q, false),
  });

  // The disabled risk factors come from the separate risk config API, in their own query:
  // a risk-config failure must not fail the whole modal. While it fails the modal stays
  // usable with no factors disabled (display only; they are not saved from here), and a
  // transient failure asks again until the API is back.
  const riskConfigQuery = useQuery({
    queryKey: RISK_CONFIG_QUERY_KEY,
    queryFn: ({ signal }) => service.getRiskFactorConfig(signal),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    refetchInterval: (q) => transientErrorRefetchInterval(q, false),
  });

  const baseConfig = query.data;
  const riskConfigResponse = riskConfigQuery.data;
  const riskConfigSettled = riskConfigQuery.status !== "pending";
  const config = useMemo(() => {
    // As before, the modal gets its config once both reads have answered (the risk
    // config may have failed).
    if (!baseConfig || !riskConfigSettled) return undefined;
    // Use the first facility's config as a representative value for the modal.
    const firstEntry = riskConfigResponse
      ? Object.values(riskConfigResponse)[0]
      : null;
    return {
      ...baseConfig,
      disabledRiskFactors: firstEntry?.custom_config?.disabled_risk_factors ?? [],
    };
  }, [baseConfig, riskConfigResponse, riskConfigSettled]);

  const mutation = useMutation({
    mutationFn: async (config: ConfigurationState) => {
      const payload = mapConfigToApiRequest(config);
      await service.saveResidentTabConfiguration(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    config,
    isLoading: query.isLoading || (enabled && riskConfigQuery.isLoading),
    error: query.error,
    saveConfig: async (config: ConfigurationState) => {
      await mutation.mutateAsync(config);
    },
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
